import { useEffect } from "react";
import { useNotificationMutation } from "../../../domains/notifications/hooks/core/use-notification-mutation";
import type { UserNotification } from "../../../domains/notifications/types";

/**
 * Hook to handle Firebase Cloud Messaging notifications
 * and sync them with the dashboard notification system
 */
export function useFCMNotifications() {
  const { markAsRead } = useNotificationMutation();

  useEffect(() => {
    // Listen for FCM notifications received in foreground
    const handleFCMNotification = (event: CustomEvent<UserNotification>) => {
      const _notification = event.detail;
    };

    // Add event listener for FCM notifications
    window.addEventListener("fcm-notification-received", handleFCMNotification as EventListener);

    // Listen for notification clicks from service worker
    const handleNotificationClick = (event: MessageEvent) => {
      if (event.data && event.data.type === "NOTIFICATION_CLICK") {
        const { notificationId, url } = event.data;

        // Mark notification as read when clicked
        if (notificationId) {
          markAsRead([notificationId]).catch((error) => {
            console.error("Failed to mark FCM notification as read:", error);
          });
        }

        // Handle navigation
        if (url && url !== "/") {
          // Use React Router navigation if available
          if (window.location.pathname !== url) {
            window.location.href = url;
          }
        }
      }
    };

    // Listen for messages from service worker
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", handleNotificationClick);
    }

    // Cleanup
    return () => {
      window.removeEventListener(
        "fcm-notification-received",
        handleFCMNotification as EventListener
      );

      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.removeEventListener("message", handleNotificationClick);
      }
    };
  }, [markAsRead]);

  // Check if FCM is supported and permission granted
  const isFCMSupported = () => {
    return "serviceWorker" in navigator && "PushManager" in window;
  };

  const getFCMPermissionStatus = (): NotificationPermission | "unsupported" => {
    if (!isFCMSupported()) {
      return "unsupported";
    }
    return Notification.permission;
  };

  return {
    isFCMSupported,
    getFCMPermissionStatus,
  };
}
